// src/services/thumbnailCompositionApi.ts
import axios from 'axios';
import { getApiUrl } from '../config/api';

// Helper function to get authentication headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

// Helper function for FormData requests (no Content-Type header)
const getAuthHeadersForFormData = () => {
  const token = localStorage.getItem('authToken');
  const headers: Record<string, string> = {};
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

export interface ImageAdjustments {
  contrast?: number; // -100 to 100
  brightness?: number; // -100 to 100
  blackAndWhite?: boolean;
  position?: { x: number; y: number };
  scale?: { scaleX: number; scaleY: number };
  rotation?: number; // 0-360 degrees
}

export interface TextElement {
  id: string;
  text: string;
  position: { x: number; y: number };
  style: {
    fontSize: number; // 8-200 pixels
    fontFamily: string;
    color: string; // Hex color
    textAlign: 'left' | 'center' | 'right';
    shadow?: {
      enabled: boolean;
      color: string;
      offsetX: number;
      offsetY: number;
      blur: number;
    };
    border?: {
      enabled: boolean;
      size: number;
      color: string;
    };
    background?: {
      enabled: boolean;
      color: string;
      opacity: number; // 0-100
      padding: number;
    };
  };
}

export interface ShapeElement {
  id: string;
  type: 'rectangle' | 'circle';
  position: { x: number; y: number };
  dimensions: { width: number; height: number };
  style: {
    fillColor: string;
    fillOpacity: number; // 0-100
    borderColor?: string;
    borderWidth?: number;
  };
  rotation?: number; // 0-360 degrees
}

export interface AssetElement {
  id: string;
  assetUrl: string;
  position: { x: number; y: number };
  dimensions: { width: number; height: number };
  opacity: number; // 0-100
  rotation?: number; // 0-360 degrees
}

export interface ThumbnailComposition {
  projectId: string;
  baseImageUrl: string;
  imageAdjustments?: ImageAdjustments;
  textElements?: TextElement[];
  shapeElements?: ShapeElement[];
  assetElements?: AssetElement[];
  canvasSettings?: {
    backgroundColor?: string;
    backgroundOpacity?: number; // 0-100
  };
}

export interface CompositionResult {
  projectId: string;
  s3Url: string;
}

export interface GenerationProgress {
  status: 'processing' | 'completed' | 'error';
  progress: number; // 0-100
  message: string;
  result?: CompositionResult;
  error?: string;
}

/**
 * Compose thumbnail with all elements (text, shapes, assets, adjustments)
 * This creates the final production-ready thumbnail
 */
export const composeThumbnail = async (
  composition: ThumbnailComposition
): Promise<CompositionResult> => {
  try {
    const response = await axios.post(getApiUrl('/thumbnail/compose'), composition, {
      timeout: 60000, // 60 second timeout for composition
      headers: getAuthHeaders()
    });
    
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Composition failed');
    }
  } catch (error) {
    console.error('Thumbnail composition error:', error);
    throw error;
  }
};

/**
 * Upload asset for thumbnail composition
 */
export const uploadThumbnailAsset = async (
  projectId: string,
  file: File,
  assetName?: string
): Promise<{
  assetId: string;
  assetUrl: string;
  metadata: {
    originalName: string;
    assetName: string;
    dimensions: { width: number; height: number };
    fileSize: number;
    mimeType: string;
  };
}> => {
  try {
    const formData = new FormData();
    formData.append('projectId', projectId);
    formData.append('asset', file);
    if (assetName) {
      formData.append('assetName', assetName);
    }

    const response = await axios.post(getApiUrl('/thumbnail/upload-asset'), formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...getAuthHeadersForFormData()
      },
      timeout: 30000
    });

    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Asset upload failed');
    }
  } catch (error) {
    console.error('Asset upload error:', error);
    throw error;
  }
};

/**
 * Get project assets
 */
export const getProjectAssets = async (projectId: string) => {
  try {
    const response = await axios.get(getApiUrl(`/thumbnail/projects/${projectId}/assets`), {
      headers: getAuthHeaders()
    });
    
    if (response.data.success) {
      return response.data.data.assets;
    } else {
      throw new Error(response.data.message || 'Failed to fetch assets');
    }
  } catch (error) {
    console.error('Get assets error:', error);
    throw error;
  }
};

/**
 * Get project thumbnails
 */
export const getProjectThumbnails = async (projectId: string) => {
  try {
    const response = await axios.get(getApiUrl(`/thumbnail/projects/${projectId}/thumbnails`), {
      headers: getAuthHeaders()
    });
    
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Failed to fetch thumbnails');
    }
  } catch (error) {
    console.error('Get thumbnails error:', error);
    throw error;
  }
};

/**
 * Get available fonts for text elements
 */
export const getAvailableFonts = async (): Promise<string[]> => {
  // Return the fonts available according to the backend documentation
  return [
    'Bebas Neue',
    'Anton', 
    'Lilita One',
    'Alfa Slab One',
    'Rubik Mono One',
    'Passion One',
    'Concert One',
    'Luckiest Guy',
    'Titan One',
    'Paytone One',
    'Bangers',
    'Geologica',
    'Atma',
    'Chewy',
    'Ranchers',
    'Arial'
  ];
};

/**
 * Upload a pre-composed thumbnail blob to S3
 * This is used when the thumbnail is already rendered on the frontend canvas
 */
export const uploadComposedThumbnail = async (
  projectId: string,
  thumbnailBlob: Blob,
  composition?: ThumbnailComposition
): Promise<CompositionResult> => {
  try {
    const formData = new FormData();
    formData.append('thumbnail', thumbnailBlob, 'thumbnail.png');
    formData.append('projectId', projectId);
    
    if (composition) {
      formData.append('composition', JSON.stringify(composition));
    }

    const response = await axios.post(getApiUrl('/thumbnail/upload-composed'), formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...getAuthHeadersForFormData()
      },
      timeout: 60000 // 60 second timeout for upload
    });
    
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Upload failed');
    }
  } catch (error) {
    console.error('Composed thumbnail upload error:', error);
    throw error;
  }
};

/**
 * Health check for thumbnail service
 */
export const checkThumbnailServiceHealth = async () => {
  try {
    const response = await axios.get(getApiUrl('/thumbnail/health'));
    return response.data;
  } catch (error) {
    console.error('Health check error:', error);
    throw error;
  }
};

// ========== NEW USER ASSET API FUNCTIONS ==========

export interface UserAsset {
  id: string;
  assetName: string;
  originalName: string;
  assetUrl: string;
  category: string;
  dimensions: { width: number; height: number } | null;
  fileSize: number;
  mimeType: string;
  createdAt: string;
  metadata: any;
}

/**
 * Get all assets for a user
 */
export const getUserAssets = async (userId: string, category?: string, search?: string): Promise<UserAsset[]> => {
  try {
    if (!userId || typeof userId !== 'string') {
      throw new Error('userId must be a non-empty string');
    }
    
    let url = getApiUrl(`/thumbnail/user/${encodeURIComponent(userId)}/assets`);
    const params = new URLSearchParams();
    
    if (category) params.append('category', category);
    if (search) params.append('search', search);
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await axios.get(url, {
      headers: getAuthHeaders()
    });
    
    if (response.data.success) {
      return response.data.data.assets;
    } else {
      throw new Error(response.data.message || 'Failed to fetch user assets');
    }
  } catch (error) {
    console.error('Get user assets error:', error);
    throw error;
  }
};

/**
 * Upload asset to user account (reusable across projects)
 */
export const uploadUserAsset = async (
  userId: string,
  file: File,
  assetName?: string,
  category?: string
): Promise<{
  assetId: string;
  assetUrl: string;
  metadata: {
    originalName: string;
    assetName: string;
    dimensions: { width: number; height: number } | null;
    fileSize: number;
    mimeType: string;
    category: string;
  };
}> => {
  try {
    if (!userId || typeof userId !== 'string') {
      throw new Error('userId must be a non-empty string');
    }
    
    const formData = new FormData();
    formData.append('asset', file);
    if (assetName) {
      formData.append('assetName', assetName);
    }
    if (category) {
      formData.append('category', category);
    }

    const uploadUrl = getApiUrl(`/thumbnail/user/${encodeURIComponent(userId)}/assets`);

    const response = await axios.post(uploadUrl, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...getAuthHeadersForFormData()
      },
      timeout: 30000
    });

    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'User asset upload failed');
    }
  } catch (error) {
    console.error('User asset upload error:', error);
    throw error;
  }
};

/**
 * Delete user asset
 */
export const deleteUserAsset = async (userId: string, assetId: string): Promise<void> => {
  try {
    const response = await axios.delete(getApiUrl(`/thumbnail/user/${userId}/assets/${assetId}`), {
      headers: getAuthHeaders()
    });
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete user asset');
    }
  } catch (error) {
    console.error('Delete user asset error:', error);
    throw error;
  }
};

/**
 * Get user assets by category
 */
export const getUserAssetsByCategory = async (userId: string, category: string): Promise<UserAsset[]> => {
  try {
    const response = await axios.get(getApiUrl(`/thumbnail/user/${userId}/assets/${category}`), {
      headers: getAuthHeaders()
    });
    
    if (response.data.success) {
      return response.data.data.assets;
    } else {
      throw new Error(response.data.message || 'Failed to fetch user assets by category');
    }
  } catch (error) {
    console.error('Get user assets by category error:', error);
    throw error;
  }
};